import React, { useState } from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';

// 页面容器
const PageContainer = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '3rem',
  padding: '2rem 0'
});

// 页面标题区块
const TitleSection = styled('section', {
  textAlign: 'center',
  marginBottom: '2rem'
});

// 标题
const Title = styled('h1', {
  fontSize: '2.5rem',
  fontWeight: 'bold',
  marginBottom: '1rem',
  '@media (max-width: 768px)': {
    fontSize: '2rem'
  }
});

// 副标题
const Subtitle = styled('p', {
  fontSize: '1.2rem',
  maxWidth: '800px',
  margin: '0 auto',
  color: '$textSecondary',
  lineHeight: 1.6
});

// 计算器容器
const CalculatorContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '2rem',
  maxWidth: '1000px',
  margin: '0 auto',
  padding: '2rem',
  backgroundColor: '$backgroundSecondary',
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  '@media (max-width: 768px)': {
    padding: '1.5rem'
  }
});

// 计算器标题
const CalculatorTitle = styled('h2', {
  fontSize: '1.75rem',
  fontWeight: 'bold',
  marginBottom: '1.5rem'
});

// 计算器表单
const CalculatorForm = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '2rem',
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr'
  }
});

// 表单组容器
const FormGroup = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem'
});

// 表单标签
const Label = styled('label', {
  fontSize: '1rem',
  fontWeight: 'medium'
});

// 选择输入
const Select = styled('select', {
  padding: '0.75rem',
  borderRadius: '8px',
  border: '1px solid $border',
  backgroundColor: '$backgroundPrimary',
  color: '$textPrimary',
  fontSize: '1rem',
  width: '100%'
});

// 数字输入
const NumberInput = styled('div', {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  position: 'relative'
});

// 输入框
const Input = styled('input', {
  width: '100%',
  padding: '0.75rem',
  borderRadius: '8px',
  border: '1px solid $border',
  backgroundColor: '$backgroundPrimary',
  color: '$textPrimary',
  fontSize: '1rem',
  '&:focus': {
    outline: 'none',
    borderColor: '$primary'
  }
});

// 输入框单位
const InputUnit = styled('span', {
  position: 'absolute',
  right: '12px',
  color: '$textSecondary',
  pointerEvents: 'none'
});

// 范围滑块
const RangeSlider = styled('input', {
  width: '100%',
  height: '6px',
  borderRadius: '3px',
  background: 'linear-gradient(90deg, $primary 0%, $primary 50%, $border 50%, $border 100%)',
  appearance: 'none',
  outline: 'none',
  '&::-webkit-slider-thumb': {
    appearance: 'none',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    backgroundColor: '$primary',
    cursor: 'pointer'
  }
});

// 结果容器
const ResultsContainer = styled('div', {
  marginTop: '2rem',
  padding: '1.5rem',
  backgroundColor: '$backgroundPrimary',
  borderRadius: '12px',
  border: '1px solid $border'
});

// 结果标题
const ResultTitle = styled('h3', {
  fontSize: '1.25rem',
  fontWeight: 'bold',
  marginBottom: '1rem'
});

// 结果项目列表
const ResultsList = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem'
});

// 结果项目
const ResultItem = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '0.75rem 0',
  borderBottom: '1px solid $border',
  '&:last-child': {
    borderBottom: 'none',
    fontWeight: 'bold'
  }
});

// 计算结果描述
const ResultLabel = styled('span', {
  color: '$textPrimary'
});

// 计算结果值
const ResultValue = styled('span', {
  fontWeight: 'medium'
});

// 总价容器
const TotalContainer = styled('div', {
  marginTop: '1.5rem',
  padding: '1rem',
  backgroundColor: '$primary',
  color: 'white',
  borderRadius: '8px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
});

// 总价标签
const TotalLabel = styled('span', {
  fontSize: '1.1rem',
  fontWeight: 'medium'
});

// 总价值
const TotalValue = styled('span', {
  fontSize: '1.5rem',
  fontWeight: 'bold'
});

// 分割线
const Divider = styled('hr', {
  border: 'none',
  borderTop: '1px solid $border',
  margin: '2rem 0'
});

// 备注部分
const Notes = styled('div', {
  fontSize: '0.875rem',
  color: '$textSecondary',
  lineHeight: 1.6
});

// 备注列表
const NotesList = styled('ul', {
  paddingLeft: '1.5rem',
  marginTop: '0.5rem'
});

// 备注项目
const NoteItem = styled('li', {
  marginBottom: '0.5rem'
});

/**
 * 矿机托管价格计算器组件
 * 用于计算不同型号矿机、不同电费和托管时长的托管费用
 */
const MiningHotelCalculator: React.FC = () => {
  const { t } = useTranslation();
  
  // 矿机型号列表
  const minerModels = [
    { id: 'antminer-s19', name: 'Antminer S19', power: 3250, hashrate: 95 },
    { id: 'antminer-s19-pro', name: 'Antminer S19 Pro', power: 3250, hashrate: 110 },
    { id: 'antminer-s19j-pro', name: 'Antminer S19j Pro', power: 3100, hashrate: 104 },
    { id: 'whatsminer-m30s', name: 'Whatsminer M30S', power: 3400, hashrate: 88 },
    { id: 'whatsminer-m30s-plus', name: 'Whatsminer M30S+', power: 3400, hashrate: 100 },
    { id: 'whatsminer-m30s-plus-plus', name: 'Whatsminer M30S++', power: 3472, hashrate: 112 },
    { id: 'avalon-1246', name: 'Avalon 1246', power: 3420, hashrate: 90 },
    { id: 'custom', name: t('miningHotel.calculator.customMiner'), power: 3000, hashrate: 100 }
  ];
  
  // 托管套餐列表
  const hostingPlans = [
    { id: 'standard', name: t('miningHotel.calculator.standardPlan'), pricePerKwh: 0.06 },
    { id: 'premium', name: t('miningHotel.calculator.premiumPlan'), pricePerKwh: 0.055 },
    { id: 'enterprise', name: t('miningHotel.calculator.enterprisePlan'), pricePerKwh: 0.05 }
  ];
  
  // 状态管理
  const [selectedMiner, setSelectedMiner] = useState(minerModels[0].id);
  const [selectedPlan, setSelectedPlan] = useState(hostingPlans[0].id);
  const [minerCount, setMinerCount] = useState(1);
  const [contractLength, setContractLength] = useState(12); // 默认12个月
  const [customPower, setCustomPower] = useState(3000);
  const [customHashrate, setCustomHashrate] = useState(100);
  
  // 获取当前选择的矿机信息
  const currentMiner = selectedMiner === 'custom' 
    ? { id: 'custom', name: t('miningHotel.calculator.customMiner'), power: customPower, hashrate: customHashrate }
    : minerModels.find(m => m.id === selectedMiner) || minerModels[0];
  
  // 获取当前选择的套餐信息
  const currentPlan = hostingPlans.find(p => p.id === selectedPlan) || hostingPlans[0];
  
  // 计算每台矿机每月电费
  const calculateMonthlyPowerCost = () => {
    const powerInKw = currentMiner.power / 1000;
    const hoursInMonth = 30 * 24;
    return powerInKw * hoursInMonth * currentPlan.pricePerKwh;
  };
  
  // 计算每台矿机每月托管费
  const calculateMonthlyHostingFee = () => {
    // 基础托管费 - 按照每台矿机每月收费
    const baseFee = 30; // 每台每月的基础托管费，美元
    
    // 合约时长折扣
    let contractDiscount = 0;
    if (contractLength >= 24) {
      contractDiscount = 0.1; // 24个月以上9折
    } else if (contractLength >= 12) {
      contractDiscount = 0.05; // 12个月以上95折
    }
    
    return baseFee * (1 - contractDiscount);
  };
  
  // 计算总托管费用
  const calculateTotalHostingCost = () => {
    const monthlyPowerCost = calculateMonthlyPowerCost();
    const monthlyHostingFee = calculateMonthlyHostingFee();
    const monthlyTotalPerMiner = monthlyPowerCost + monthlyHostingFee;
    
    return {
      monthlyPowerCost,
      monthlyHostingFee,
      monthlyTotalPerMiner,
      monthlyTotal: monthlyTotalPerMiner * minerCount,
      contractTotal: monthlyTotalPerMiner * minerCount * contractLength
    };
  };
  
  // 计算结果
  const costResults = calculateTotalHostingCost();
  
  // 处理输入框更改
  const handleCustomInputChange = (event: React.ChangeEvent<HTMLInputElement>, type: 'power' | 'hashrate') => {
    const value = parseInt(event.target.value, 10) || 0;
    if (type === 'power') {
      setCustomPower(value);
    } else {
      setCustomHashrate(value);
    }
  };
  
  return (
    <PageContainer>
      <TitleSection>
        <Title>{t('miningHotel.calculator.title')}</Title>
        <Subtitle>{t('miningHotel.calculator.subtitle')}</Subtitle>
      </TitleSection>
      
      <CalculatorContainer>
        <CalculatorTitle>{t('miningHotel.calculator.estimateYourCost')}</CalculatorTitle>
        
        <CalculatorForm>
          {/* 矿机型号选择 */}
          <FormGroup>
            <Label htmlFor="miner-model" id="miner-model-label">{t('miningHotel.calculator.minerModel')}</Label>
            <Select 
              id="miner-model" 
              value={selectedMiner}
              onChange={(e) => setSelectedMiner(e.target.value)}
              aria-labelledby="miner-model-label"
            >
              {minerModels.map(model => (
                <option key={model.id} value={model.id}>
                  {model.name} ({model.hashrate} TH/s, {model.power}W)
                </option>
              ))}
            </Select>
          </FormGroup>
          
          {/* 托管套餐选择 */}
          <FormGroup>
            <Label htmlFor="hosting-plan" id="hosting-plan-label">{t('miningHotel.calculator.hostingPlan')}</Label>
            <Select 
              id="hosting-plan" 
              value={selectedPlan}
              onChange={(e) => setSelectedPlan(e.target.value)}
              aria-labelledby="hosting-plan-label"
            >
              {hostingPlans.map(plan => (
                <option key={plan.id} value={plan.id}>
                  {plan.name} (${plan.pricePerKwh}/kWh)
                </option>
              ))}
            </Select>
          </FormGroup>
          
          {/* 矿机数量选择 */}
          <FormGroup>
            <Label htmlFor="miner-count" id="miner-count-label">{t('miningHotel.calculator.minerCount')}</Label>
            <NumberInput>
              <Input 
                id="miner-count" 
                type="number" 
                min="1" 
                max="1000"
                value={minerCount}
                onChange={(e) => setMinerCount(parseInt(e.target.value, 10) || 1)}
              />
              <InputUnit>{t('miningHotel.calculator.units')}</InputUnit>
            </NumberInput>
            <RangeSlider 
              type="range" 
              min="1" 
              max="100" 
              value={minerCount}
              onChange={(e) => setMinerCount(parseInt(e.target.value, 10))}
              style={{ 
                background: `linear-gradient(90deg, var(--colors-primary) 0%, var(--colors-primary) ${minerCount}%, var(--colors-border) ${minerCount}%, var(--colors-border) 100%)` 
              }}
              aria-labelledby="miner-count-label"
            />
          </FormGroup>
          
          {/* 合约时长选择 */}
          <FormGroup>
            <Label htmlFor="contract-length" id="contract-length-label">{t('miningHotel.calculator.contractLength')}</Label>
            <NumberInput>
              <Input 
                id="contract-length" 
                type="number" 
                min="1" 
                max="36"
                value={contractLength}
                onChange={(e) => setContractLength(parseInt(e.target.value, 10) || 1)}
              />
              <InputUnit>{t('miningHotel.calculator.months')}</InputUnit>
            </NumberInput>
            <RangeSlider 
              type="range" 
              min="1" 
              max="36" 
              value={contractLength}
              onChange={(e) => setContractLength(parseInt(e.target.value, 10))}
              style={{ 
                background: `linear-gradient(90deg, var(--colors-primary) 0%, var(--colors-primary) ${contractLength / 36 * 100}%, var(--colors-border) ${contractLength / 36 * 100}%, var(--colors-border) 100%)` 
              }}
              aria-labelledby="contract-length-label"
            />
          </FormGroup>
          
          {/* 自定义矿机参数（仅当选择"自定义矿机"时显示） */}
          {selectedMiner === 'custom' && (
            <>
              <FormGroup>
                <Label htmlFor="custom-power">{t('miningHotel.calculator.customPower')}</Label>
                <NumberInput>
                  <Input 
                    id="custom-power" 
                    type="number" 
                    min="1000" 
                    max="5000"
                    value={customPower}
                    onChange={(e) => handleCustomInputChange(e, 'power')}
                  />
                  <InputUnit>W</InputUnit>
                </NumberInput>
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="custom-hashrate">{t('miningHotel.calculator.customHashrate')}</Label>
                <NumberInput>
                  <Input 
                    id="custom-hashrate" 
                    type="number" 
                    min="10" 
                    max="200"
                    value={customHashrate}
                    onChange={(e) => handleCustomInputChange(e, 'hashrate')}
                  />
                  <InputUnit>TH/s</InputUnit>
                </NumberInput>
              </FormGroup>
            </>
          )}
        </CalculatorForm>
        
        <Divider />
        
        {/* 计算结果 */}
        <ResultsContainer>
          <ResultTitle>{t('miningHotel.calculator.costBreakdown')}</ResultTitle>
          <ResultsList>
            <ResultItem>
              <ResultLabel>{t('miningHotel.calculator.monthlyPowerCost')}</ResultLabel>
              <ResultValue>${costResults.monthlyPowerCost.toFixed(2)} / {t('miningHotel.calculator.minerPerMonth')}</ResultValue>
            </ResultItem>
            
            <ResultItem>
              <ResultLabel>{t('miningHotel.calculator.monthlyHostingFee')}</ResultLabel>
              <ResultValue>${costResults.monthlyHostingFee.toFixed(2)} / {t('miningHotel.calculator.minerPerMonth')}</ResultValue>
            </ResultItem>
            
            <ResultItem>
              <ResultLabel>{t('miningHotel.calculator.monthlyTotalPerMiner')}</ResultLabel>
              <ResultValue>${costResults.monthlyTotalPerMiner.toFixed(2)} / {t('miningHotel.calculator.minerPerMonth')}</ResultValue>
            </ResultItem>
            
            <ResultItem>
              <ResultLabel>{t('miningHotel.calculator.monthlyTotalAllMiners')}</ResultLabel>
              <ResultValue>${costResults.monthlyTotal.toFixed(2)} / {t('miningHotel.calculator.month')}</ResultValue>
            </ResultItem>
          </ResultsList>
          
          <TotalContainer>
            <TotalLabel>{t('miningHotel.calculator.contractTotal')}</TotalLabel>
            <TotalValue>${costResults.contractTotal.toFixed(2)}</TotalValue>
          </TotalContainer>
        </ResultsContainer>
        
        <Notes>
          <p>{t('miningHotel.calculator.notesTitle')}</p>
          <NotesList>
            <NoteItem>{t('miningHotel.calculator.note1')}</NoteItem>
            <NoteItem>{t('miningHotel.calculator.note2')}</NoteItem>
            <NoteItem>{t('miningHotel.calculator.note3')}</NoteItem>
            <NoteItem>{t('miningHotel.calculator.note4')}</NoteItem>
          </NotesList>
        </Notes>
      </CalculatorContainer>
    </PageContainer>
  );
};

export default MiningHotelCalculator; 